/**
 * @file PostHog client, wrapped so the rest of the game never imports the SDK.
 *
 * Three things this wrapper buys, all of which the raw SDK would cost us:
 *
 * 1. **It is always safe to call.** No key, no `window`, jsdom under vitest, an
 *    SDK that failed to download — every one of those degrades to a no-op
 *    instead of throwing inside a game loop. Analytics must never be able to
 *    kill a frame.
 * 2. **The SDK loads off the critical path.** `posthog-js` arrives through a
 *    dynamic import, so it lands in its own chunk and the game boots without
 *    waiting for it. Events captured before it resolves are queued here and
 *    replayed in order, so `game_loaded` is never the event you lose.
 * 3. **Super properties are the seam.** `register()` stamps run and level
 *    context onto every event, which is what lets the bridge stay a flat list
 *    of handlers instead of threading a context object through all of them.
 */

import { ANALYTICS, analyticsConfigured, analyticsEnvironment } from '../config/Analytics.js';

/** Queue cap. A queue this long means the SDK is never coming; stop growing. */
const MAX_QUEUE = 200;

export class Analytics {
  /**
   * @param {object} [options] Setup.
   * @param {typeof ANALYTICS} [options.config] Settings override.
   * @param {() => Promise<{ default: any }>} [options.loader] SDK importer, so a
   *   test can inject a stub without touching the network.
   * @param {boolean} [options.configured] Force the enabled decision, for tests.
   */
  constructor({ config = ANALYTICS, loader, configured } = {}) {
    /** @type {typeof ANALYTICS} @private */
    this._config = config;
    /** @type {() => Promise<{ default: any }>} @private */
    this._loader = loader ?? (() => import('posthog-js'));
    /**
     * Whether this instance will ever send anything. Decided once, at
     * construction, so every call site is a cheap boolean check.
     * @type {boolean}
     * @private
     */
    this._enabled =
      (configured ?? analyticsConfigured()) && typeof window !== 'undefined' && typeof document !== 'undefined';
    /** @type {any} The SDK, once loaded. @private */
    this._posthog = null;
    /** @type {Array<[string, Record<string, any>]>} @private */
    this._queue = [];
    /** @type {Record<string, any>} Super properties applied on load. @private */
    this._pending = {};
    /** @type {boolean} @private */
    this._started = false;
    /** @type {number} Events dropped because the queue filled. @private */
    this._dropped = 0;
  }

  /** @returns {boolean} True when this instance actually reports. */
  get enabled() {
    return this._enabled;
  }

  /** @returns {any} The underlying SDK, or null before it loads. */
  get client() {
    return this._posthog;
  }

  /**
   * Loads and initialises the SDK. Safe to call once; later calls are ignored.
   * Never rejects — a failed download leaves the wrapper inert.
   * @returns {Promise<void>}
   */
  async init() {
    if (!this._enabled || this._started) return;
    this._started = true;

    let sdk;
    try {
      sdk = (await this._loader()).default;
    } catch (error) {
      // The game does not care. Say so once, then stay quiet forever.
      console.warn('[analytics] posthog-js failed to load; instrumentation is off', error);
      this._enabled = false;
      this._queue.length = 0;
      return;
    }

    try {
      sdk.init(this._config.key, {
        api_host: this._config.host,
        ui_host: this._config.uiHost,
        debug: this._config.debug,
        // The `defaults` pin is what keeps a future SDK release from silently
        // changing what gets captured under us.
        defaults: '2026-05-30',
        // Clicks on the HUD, the menu nav and the pause popup, for free.
        autocapture: true,
        // Explicitly off: this is a game. Clicking fast at a moving target is
        // the point, not a symptom of a broken UI, so every rage-click would be
        // a false positive. Dead clicks go with it for the same reason.
        rageclick: false,
        capture_dead_clicks: false,
        // Session duration and visit counts.
        capture_pageview: true,
        capture_pageleave: true,
        // Core Web Vitals + load timings, so a heavy build shows up as a metric
        // rather than as a vague complaint.
        capture_performance: true,
        // Unhandled errors and rejections become `$exception`, feeding PostHog
        // error tracking without a second SDK.
        capture_exceptions: true,
        // Answered "off" when this was set up: canvas recording costs real
        // frames on a 60 fps WebGL scene and mostly captures DOM chrome.
        disable_session_recording: true,
        disable_surveys: true,
        // Anonymous players still need a person record — every metric here is
        // per-player (runs survived, best floor), not per-event.
        person_profiles: 'always',
        persistence: 'localStorage+cookie',
        // PostHog drops capture entirely for anything it reads as a bot, and
        // an automated browser is one: headless UA, `navigator.webdriver`. That
        // is exactly right for production — a crawler must not land in the
        // funnel — and exactly wrong for a dev build, where the automated
        // browser *is* how the instrumentation gets smoke-tested. So the filter
        // is bypassed in dev only, and dev events are tagged
        // `environment: development` anyway.
        opt_out_useragent_filter: Boolean(import.meta.env?.DEV)
      });
      this._posthog = sdk;
    } catch (error) {
      console.warn('[analytics] posthog-js failed to initialise; instrumentation is off', error);
      this._enabled = false;
      this._queue.length = 0;
      return;
    }

    // Environment first, so the very first replayed event is already tagged.
    this._posthog.register({ environment: analyticsEnvironment(), ...this._pending });
    this._pending = {};

    if (this._dropped > 0) this._posthog.capture('analytics_queue_overflow', { dropped: this._dropped });
    for (const [event, properties] of this._queue) this._posthog.capture(event, properties);
    this._queue.length = 0;
  }

  /**
   * Sends an event, or queues it when the SDK has not arrived yet.
   * @param {string} event Event name, snake_case.
   * @param {Record<string, any>} [properties] Event properties.
   * @returns {void}
   */
  capture(event, properties = {}) {
    if (!this._enabled) return;
    if (this._posthog) {
      this._posthog.capture(event, properties);
      return;
    }
    if (this._queue.length >= MAX_QUEUE) {
      this._dropped++;
      return;
    }
    this._queue.push([event, properties]);
  }

  /**
   * Adds super properties — stamped onto every subsequent event, including the
   * autocaptured ones, which is how a `$autocapture` click knows what floor the
   * player was on.
   * @param {Record<string, any>} properties Properties to attach.
   * @returns {void}
   */
  register(properties) {
    if (!this._enabled) return;
    if (this._posthog) this._posthog.register(properties);
    else Object.assign(this._pending, properties);
  }

  /**
   * Drops a super property.
   * @param {string} key Property name.
   * @returns {void}
   */
  unregister(key) {
    if (!this._enabled) return;
    if (this._posthog) this._posthog.unregister(key);
    else delete this._pending[key];
  }

  /**
   * Updates the person record behind the current player.
   * @param {Record<string, any>} [set] Properties to overwrite.
   * @param {Record<string, any>} [setOnce] Properties to write only if absent.
   * @returns {void}
   */
  person(set, setOnce) {
    if (!this._enabled) return;
    // `$set` on an event rather than `setPersonProperties`, so a person update
    // never costs an extra request of its own.
    this.capture('$set', { $set: set ?? {}, $set_once: setOnce ?? {} });
  }

  /**
   * Flushes anything batched. Used before a navigation the SDK cannot see.
   * @returns {void}
   */
  flush() {
    this._posthog?.flush?.();
  }

  /**
   * Stops reporting and releases the SDK reference.
   * @returns {void}
   */
  dispose() {
    this.flush();
    this._enabled = false;
    this._posthog = null;
    this._queue.length = 0;
    this._pending = {};
  }
}
